// ============================================================
// VCS.DeviceLink -- Anmeldung auf neuem Geraet per QR-Code
// ------------------------------------------------------------
// Zwei Rollen:
//   * Altes Geraet (eingeloggt): zeigt einen QR-Code. Es erzeugt ein
//     Zufalls-Secret, verschluesselt damit lokal den privaten E2E-Schluessel
//     und laedt nur den verschluesselten Blob auf den Server (/auth/qr-create).
//     Das Secret verlaesst das Geraet ausschliesslich ueber den Bildschirm.
//   * Neues Geraet (nicht eingeloggt): scannt den QR-Code, loest den
//     Transfer-Slot ein (/auth/qr-consume), entschluesselt den Blob mit dem
//     Secret aus dem QR und ist danach ohne Passwort eingeloggt -- inklusive
//     funktionierender E2E-Verschluesselung.
//
// Der Server bleibt blind: er sieht nie das Secret und nie den Klartext-Key.
// Krypto-Primitiven kommen aus dem globalen E2E-Objekt (messenger/js/e2e.js).
// QR-Encode/-Decode laeuft ueber den lib-agnostischen Adapter am Dateiende.
// ============================================================
window.VCS = window.VCS || {};

VCS.DeviceLink = class {

    // ── Konstruktor ──────────────────────────────────────────

    constructor() {
        // Laufzeit-Status Scanner (neues Geraet)
        this._scanActive = false;
        this._scanStream = null;
        this._scanRaf    = null;
        this._detector   = null;  // BarcodeDetector-Instanz, lazy

        // Laufzeit-Status QR-Anzeige (altes Geraet)
        this._qrCountdownTimer = null;

        // View-Cache
        this._view = {
            // Auth-Screen (neues Geraet)
            btnLoginWithQr:      $('#btnLoginWithQr'),
            // Einstellungen (altes Geraet)
            btnLinkNewDevice:    $('#btnLinkNewDevice'),

            // Scan-Overlay (neues Geraet)
            scanOverlay:         $('#deviceLinkScanOverlay'),
            scanVideo:           $('#deviceLinkScanVideo'),
            scanStatus:          $('#deviceLinkScanStatus'),
            scanCancel:          $('#deviceLinkScanCancel'),

            // QR-Anzeige-Overlay (altes Geraet)
            qrOverlay:           $('#deviceLinkQrOverlay'),
            qrHolder:            $('#deviceLinkQrHolder'),
            qrCountdown:         $('#deviceLinkQrCountdown'),
            qrStatus:            $('#deviceLinkQrStatus'),
            qrClose:             $('#deviceLinkQrClose'),
        };
    }

    // ── Init / Wiring ────────────────────────────────────────

    init() {
        const v = this._view;

        v.btnLoginWithQr.on('click', () => this.startScan());
        v.btnLinkNewDevice.on('click', () => this.showLinkQr());
        v.scanCancel.on('click', () => this._stopScan());
        v.qrClose.on('click', () => this._stopQrDisplay());

        // Overlay-Hintergrundklick schliesst ebenfalls.
        v.scanOverlay.on('click', (e) => { if (e.target === v.scanOverlay[0]) this._stopScan(); });
        v.qrOverlay.on('click',   (e) => { if (e.target === v.qrOverlay[0])   this._stopQrDisplay(); });
    }

    _apiBase() {
        return location.origin + '/api';
    }

    // ── Altes Geraet: QR-Code erzeugen und anzeigen ──────────

    async showLinkQr() {
        if (typeof E2E === 'undefined') {
            VCS.ui?.showToast?.(t('e2eNoKey'), 'error', 4000);
            return;
        }
        const phone = VCS.state?.chatMyPhone;
        if (!phone) {
            VCS.ui?.showToast?.(t('deviceLinkNotLoggedIn'), 'error', 4000);
            return;
        }

        const { qrOverlay, qrHolder, qrStatus, qrCountdown } = this._view;
        qrHolder.empty();
        qrStatus.text(t('deviceLinkGenerating')).removeClass('hidden');
        qrCountdown.text('');
        qrOverlay.removeClass('hidden');

        let secret = '';
        try {
            // 1) Privaten E2E-Schluessel laden.
            const privateKey = await E2E.loadPrivateKey(phone);
            if (!privateKey) {
                qrStatus.text(t('deviceLinkNoKey'));
                return;
            }

            // 2) Hochentropes Transfer-Secret erzeugen (256 Bit). Bleibt nur im QR.
            secret = E2E.bufToBase64(crypto.getRandomValues(new Uint8Array(32)));

            // 3) Privaten Schluessel mit dem Secret verschluesseln (PBKDF2/AES-GCM,
            //    gleiche Primitive wie das PW-Backup). Server sieht nur den Blob.
            const blob = await E2E.encryptPrivateKeyWithPassword(secret, privateKey);

            // 4) Verschluesselten Blob im RAM-Slot des Servers ablegen.
            const res = await fetch(this._apiBase() + '/auth/qr-create', {
                method : 'POST',
                headers: VCS.chat?.authHeaders({ 'Content-Type': 'application/json' }),
                body   : JSON.stringify({ encryptedPrivateKey: JSON.stringify(blob) }),
            });
            if (!res.ok) {
                console.warn('[device-link] qr-create fehlgeschlagen status=' + res.status);
                qrStatus.text(t('deviceLinkError'));
                return;
            }
            const data = await res.json();
            if (!data.transferId) {
                qrStatus.text(t('deviceLinkError'));
                return;
            }

            // 5) QR-Payload bauen und rendern. Secret NUR hier, nie im Netz.
            const payload = JSON.stringify({ v: 1, id: data.transferId, s: secret });
            const ok = this._renderQr(payload, qrHolder[0]);
            if (!ok) {
                qrStatus.text(t('deviceLinkQrLibMissing'));
                return;
            }

            qrStatus.text(t('deviceLinkQrHint'));
            this._startCountdown(data.expiresIn || 90);
            console.log('[device-link] QR angezeigt transferId=' + data.transferId.slice(0, 8) + ' ttlSec=' + (data.expiresIn || 90));
        } catch (e) {
            console.error('[device-link] showLinkQr Fehler:', e && e.message ? e.message : e);
            qrStatus.text(t('deviceLinkError'));
        } finally {
            // Secret aus dem lokalen String helfen zu vergessen (best effort).
            secret = '';
        }
    }

    _startCountdown(seconds) {
        const { qrCountdown } = this._view;
        let remaining = seconds;
        const tick = () => {
            if (remaining <= 0) {
                qrCountdown.text('');
                this._view.qrStatus.text(t('deviceLinkExpired'));
                this._view.qrHolder.empty();
                clearInterval(this._qrCountdownTimer);
                this._qrCountdownTimer = null;
                return;
            }
            qrCountdown.text(t('deviceLinkExpiresIn').replace('{s}', String(remaining)));
            remaining -= 1;
        };
        clearInterval(this._qrCountdownTimer);
        tick();
        this._qrCountdownTimer = setInterval(tick, 1000);
    }

    _stopQrDisplay() {
        clearInterval(this._qrCountdownTimer);
        this._qrCountdownTimer = null;
        this._view.qrOverlay.addClass('hidden');
        this._view.qrHolder.empty();
    }

    // ── Neues Geraet: QR-Code scannen und einloesen ──────────

    async startScan() {
        const { scanOverlay, scanVideo, scanStatus } = this._view;
        scanStatus.text(t('deviceLinkScanHint')).removeClass('hidden');
        scanOverlay.removeClass('hidden');

        // Kamera anfordern -- Rueckkamera bevorzugt.
        try {
            this._scanStream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: { ideal: 'environment' } },
                audio: false,
            });
        } catch (e) {
            console.warn('[device-link] Kamera-Zugriff verweigert:', e && e.name ? e.name : e);
            scanStatus.text(t('deviceLinkCameraDenied'));
            return;
        }

        const videoEl = scanVideo[0];
        videoEl.srcObject = this._scanStream;
        videoEl.setAttribute('playsinline', 'true');
        try {
            await videoEl.play();
        } catch (_) {}

        this._scanActive = true;
        this._scanFrameLoop(videoEl);
    }

    async _scanFrameLoop(videoEl) {
        if (!this._scanActive) {
            return;
        }
        try {
            const text = await this._scanFrame(videoEl);
            if (text) {
                await this._handleScannedPayload(text);
                return;  // Erfolg oder Abbruch -- Loop endet.
            }
        } catch (e) {
            console.warn('[device-link] Scan-Frame Fehler:', e && e.message ? e.message : e);
        }
        // Naechster Frame.
        this._scanRaf = requestAnimationFrame(() => this._scanFrameLoop(videoEl));
    }

    async _handleScannedPayload(text) {
        let parsed;
        try {
            parsed = JSON.parse(text);
        } catch (_) {
            // Kein passender QR -- weiterscannen.
            this._scanRaf = requestAnimationFrame(() => this._scanFrameLoop(this._view.scanVideo[0]));
            return;
        }
        if (!parsed || parsed.v !== 1 || !parsed.id || !parsed.s) {
            // Fremder QR-Code -- weiterscannen.
            this._scanRaf = requestAnimationFrame(() => this._scanFrameLoop(this._view.scanVideo[0]));
            return;
        }

        // Gueltiger Transfer-QR: Scanner stoppen und einloesen.
        this._view.scanStatus.text(t('deviceLinkLinking'));
        this._teardownCamera();
        await this._consume(parsed.id, parsed.s);
    }

    async _consume(transferId, secret) {
        try {
            const res = await fetch(this._apiBase() + '/auth/qr-consume', {
                method : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body   : JSON.stringify({ transferId }),
            });
            if (res.status === 410) {
                console.warn('[device-link] qr-consume expired/consumed');
                this._view.scanStatus.text(t('deviceLinkExpired'));
                return;
            }
            if (!res.ok) {
                console.warn('[device-link] qr-consume fehlgeschlagen status=' + res.status);
                this._view.scanStatus.text(t('deviceLinkError'));
                return;
            }
            const data = await res.json();
            if (!data.sessionToken || !data.phone || !data.encryptedPrivateKey) {
                console.error('[device-link] qr-consume Antwort unvollstaendig');
                this._view.scanStatus.text(t('deviceLinkError'));
                return;
            }

            // Privaten Schluessel lokal mit dem QR-Secret entschluesseln.
            const privateKey = await E2E.decryptPrivateKeyFromBackup(secret, data.encryptedPrivateKey);
            await this._installKeys(data.phone, privateKey);

            // Session wie der normale Login-Pfad persistieren, dann Reload.
            await this._persistSessionAndReload(data.phone, data.sessionToken);
        } catch (e) {
            console.error('[device-link] _consume Fehler:', e && e.message ? e.message : e);
            this._view.scanStatus.text(t('deviceLinkError'));
        }
    }

    // Privaten Schluessel + zugehoerigen Public-Key (vom Server) lokal ablegen.
    // Analog VCS.E2E.autoRestore, aber ohne Passwort -- der Key kommt aus dem
    // QR-Transfer. Nur die aktuelle Key-Version wird uebertragen; aeltere
    // Versionen (vor einer Rotation) bleiben dem PW-Restore vorbehalten.
    async _installKeys(phone, privateKey) {
        await E2E.storePrivateKey(phone, privateKey);
        try {
            const pubRes = await fetch(this._apiBase() + '/keys/' + encodeURIComponent(phone));
            if (pubRes.ok) {
                const pubData = await pubRes.json();
                const version = pubData.version || 1;
                const pubJwk  = typeof pubData.publicKeyJwk === 'string'
                    ? JSON.parse(pubData.publicKeyJwk) : pubData.publicKeyJwk;
                if (pubJwk) {
                    await E2E.storePublicKeyJwk(phone, pubJwk);
                    await E2E.storePrivateKeyVersion(phone, version, privateKey);
                    await E2E.storePublicKeyJwkVersion(phone, version, pubJwk);
                    await E2E.setCurrentKeyVersion(phone, version);
                }
            } else {
                console.warn('[device-link] Public-Key vom Server nicht ladbar status=' + pubRes.status);
            }
        } catch (e) {
            console.warn('[device-link] _installKeys Public-Key Fehler:', e && e.message ? e.message : e);
        }
        console.log('[device-link] Private Key aus QR-Transfer installiert');
    }

    // Session-Persistierung analog handleHandoffParam() in core/app.js.
    async _persistSessionAndReload(phone, sessionToken) {
        try {
            VCS.state = VCS.state || {};
            VCS.state.chatMyPhone   = phone;
            VCS.state.chatAuthToken = sessionToken;
            localStorage.setItem('chat-auth-token', sessionToken);
            localStorage.setItem('chat-phone', phone);
            VCS.state.chatRegistered = true;
        } catch (e) {
            console.error('[device-link] localStorage Fehler:', e && e.message ? e.message : e);
        }

        // Anzeigename + Plan holen, damit die UI nach dem Reload vollstaendig ist.
        try {
            const meRes = await fetch(this._apiBase() + '/auth/me', {
                credentials: 'include',
                headers: { 'Authorization': 'Bearer ' + sessionToken },
            });
            if (meRes.ok) {
                const u = (await meRes.json())?.user;
                if (u) {
                    if (u.displayName) { localStorage.setItem('chat-name', u.displayName); }
                    if (u.plan)        { localStorage.setItem('chat-plan', u.plan); }
                    if (u.trialEndsAt) { localStorage.setItem('chat-trial-ends-at', String(u.trialEndsAt)); }
                }
            } else {
                console.warn('[device-link] /auth/me status=' + meRes.status);
            }
        } catch (e) {
            console.warn('[device-link] /auth/me Fehler:', e && e.message ? e.message : e);
        }

        console.log('[device-link] QR-Login OK phone=' + (phone || '').slice(0, 4) + '*** session=' + sessionToken.slice(0, 8));
        VCS.ui?.showToast?.(t('deviceLinkSuccess'), 'success', 2500);
        // Kurzer Moment fuer den Toast, dann sauber neu laden (wie Login-Pfad).
        setTimeout(() => location.reload(), 800);
    }

    _teardownCamera() {
        this._scanActive = false;
        if (this._scanRaf) {
            cancelAnimationFrame(this._scanRaf);
            this._scanRaf = null;
        }
        if (this._scanStream) {
            for (const track of this._scanStream.getTracks()) {
                try { track.stop(); } catch (_) {}
            }
            this._scanStream = null;
        }
        const videoEl = this._view.scanVideo[0];
        if (videoEl) {
            try { videoEl.pause(); } catch (_) {}
            videoEl.srcObject = null;
        }
    }

    _stopScan() {
        this._teardownCamera();
        this._view.scanOverlay.addClass('hidden');
    }

    // ── QR-Codec-Adapter (lib-agnostisch) ────────────────────
    // Encode nutzt die globale qrcode-generator-API (window.qrcode), sofern
    // eingebunden. Decode bevorzugt das native BarcodeDetector-API und faellt
    // sonst auf window.jsQR zurueck (fuer iOS-Safari, das kein BarcodeDetector
    // kennt). Beide Libs liegen lokal in messenger/js/ (kein Runtime-CDN).

    // Rendert den QR-Code in das Ziel-Element. Rueckgabe false, wenn keine
    // Encoder-Lib verfuegbar ist (Aufrufer zeigt dann einen Hinweis).
    _renderQr(text, holderEl) {
        if (typeof window.qrcode !== 'function' || !holderEl) {
            console.warn('[device-link] QR-Encoder (window.qrcode) fehlt');
            return false;
        }
        try {
            // typeNumber 0 = automatische Versionswahl, ECC-Level 'M'.
            const qr = window.qrcode(0, 'M');
            qr.addData(text);
            qr.make();
            // Skalierung: robuste Modulgroesse fuer Kamera-Scan.
            holderEl.innerHTML = qr.createImgTag(6, 12);
            const img = holderEl.querySelector('img');
            if (img) {
                img.setAttribute('alt', 'QR');
                img.style.width  = '240px';
                img.style.height = '240px';
            }
            return true;
        } catch (e) {
            console.error('[device-link] _renderQr Fehler:', e && e.message ? e.message : e);
            return false;
        }
    }

    // Scannt einen einzelnen Videoframe und liefert den dekodierten Text oder
    // null. Wirft nicht -- Fehler werden zu null.
    async _scanFrame(videoEl) {
        if (!videoEl || !videoEl.videoWidth) {
            return null;
        }

        // Pfad 1: natives BarcodeDetector-API (Android/Chrome).
        if ('BarcodeDetector' in window) {
            try {
                if (!this._detector) {
                    this._detector = new window.BarcodeDetector({ formats: ['qr_code'] });
                }
                const codes = await this._detector.detect(videoEl);
                if (codes && codes.length && codes[0].rawValue) {
                    return codes[0].rawValue;
                }
                return null;
            } catch (e) {
                // Detector nicht nutzbar -- auf jsQR zurueckfallen.
                this._detector = null;
            }
        }

        // Pfad 2: jsQR ueber Canvas-Pixel (iOS-Safari-Fallback).
        if (typeof window.jsQR === 'function') {
            const canvas = this._scanCanvas || (this._scanCanvas = document.createElement('canvas'));
            const w = videoEl.videoWidth;
            const h = videoEl.videoHeight;
            canvas.width  = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            ctx.drawImage(videoEl, 0, 0, w, h);
            const imageData = ctx.getImageData(0, 0, w, h);
            const result = window.jsQR(imageData.data, w, h, { inversionAttempts: 'dontInvert' });
            return result && result.data ? result.data : null;
        }

        // Keine Decode-Lib -- einmalig hinweisen.
        if (!this._scanLibWarned) {
            this._scanLibWarned = true;
            console.warn('[device-link] Keine QR-Decode-Lib (BarcodeDetector/jsQR) verfuegbar');
            this._view.scanStatus.text(t('deviceLinkQrLibMissing'));
        }
        return null;
    }
};
